import { Element, Icon } from '@/components/core';
import { ProductOfCategory } from '@/@types';
import React from 'react';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import GoogleApiProvider from '@/components/context/google-api';
import PeoopleMap from '@/components/core/components/organism/map';
import { useLingui } from '@lingui/react';
import useModals from '@/components/core/components/molecules/modals/use-modal';
import * as styles from './places.styles';

const dayTranslations = {
  monday: msg`monday`,
  tuesday: msg`tuesday`,
  wednesday: msg`wednesday`,
  thursday: msg`thursday`,
  friday: msg`friday`,
  saturday: msg`saturday`,
  sunday: msg`sunday`,
};

const PlaceDetails: React.FC<{ product: ProductOfCategory<'restaurants' | 'places'> }> = ({ product }) => {
  const { i18n } = useLingui();
  const openModal = useModals();

  return (
    <div className={styles.root}>
      <div>
        {product.extraInfo.phone && (
          <div className={styles.item}>
            <Icon className={styles.icon} name="call" />
            <a href={`tel:${product.extraInfo.phone}`} className={styles.details}>
              {product.extraInfo.phone}
            </a>
          </div>
        )}
        {product.extraInfo.location.fullAddress && (
          <div className={styles.item}>
            <span className={styles.icon}>
              <Icon name="location1" />
            </span>
            <Element
              as="a"
              target="_blank"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(product.extraInfo.location.fullAddress)}`}
              lines={1}
              className={styles.details}
            >
              {product.extraInfo.location.fullAddress}
            </Element>
          </div>
        )}
        {product.extraInfo.url && (
          <div className={styles.item}>
            <Icon name="link" className={styles.icon} />
            <a href={product.extraInfo.url} className={styles.details} target="_blank">
              <Trans>Visit website</Trans>
            </a>
          </div>
        )}
        {product.extraInfo.externalId?.covermanager && (
          <div className={styles.item}>
            <Icon name="calendar" className={styles.icon} />
            <Element
              className={styles.details}
              role="button"
              tabIndex={0}
              onClick={() => {
                openModal({
                  name: 'reservation',
                  restaurant: product as ProductOfCategory<'restaurants'>,
                });
              }}
            >
              <Trans>Book a table</Trans>
            </Element>
          </div>
        )}
      </div>
      {product.extraInfo.location.geo && (
        <div className={styles.map}>
          <GoogleApiProvider>
            <PeoopleMap disableDetails markers={[product]} center={product.extraInfo.location.geo} />
          </GoogleApiProvider>
        </div>
      )}
      {product.extraInfo.workingHours.length > 0 && (
        <div className="flex flex-col gap-4">
          <h2 className={styles.title}>
            <Trans>Working hours</Trans>
          </h2>
          <div className={styles.workingHours}>
            {product.extraInfo.workingHours.map((workingHour) => {
              return (
                <React.Fragment key={workingHour.weekDay}>
                  <div className={styles.workingHoursDay}>{i18n._(dayTranslations[workingHour.weekDay])}</div>
                  <div className={styles.workingHoursTime}>
                    {workingHour.times.length === 0 && <Trans>Closed</Trans>}
                    {workingHour.times.map(({ from, to }) => {
                      return (
                        <div key={`${from}`}>
                          {from} - {to}
                        </div>
                      );
                    })}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceDetails;
